import { React, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import List from '../components/List';
import { InboxOutlined } from '@ant-design/icons';
import { message, Upload, Tabs, Table, Modal, Input, Space, Button } from 'antd';
import { db, storage } from '../firebase';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import isUrl from 'is-url';
import { doc, setDoc } from 'firebase/firestore';
import ErrorPage from './NotFound';
import { useAtom, useSetAtom } from 'jotai';
import { papersAtom, userAtom } from '../atom';
import { useDataFetching } from '../components/helperfunction';


const { Dragger } = Upload;

function Paper() {
  const papersData = useAtom(papersAtom)[0];
  const { getData } = useDataFetching();
  const setUser = useSetAtom(userAtom);
  const maxSize = 5 * 1024 * 1024;
  const [searchParams] = useSearchParams();

  let id;
  try {
    id = decodeURIComponent(searchParams.get('id')) || 'malformedURI';
  } catch {
    id = 'malformedURI';
  }

  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useAtom(userAtom)?.[0];
  const [paperData, setPaperData] = useState([]);
  const [paperFound, setPaperFound] = useState(true);
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [okText, setOkText] = useState('OK');
  const [saveSubjectText, setSaveSubjectText] = useState('Save Subject');
  const [selectedTab, setSelectedTab] = useState('1');
  const [saveButtonLoading, setSaveButtonLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [linkData, setLinkData] = useState([{ key: '1', serialNumber: '1', columnName: 'Link 1' }]);

  useEffect(() => {
    const fetchData = async () => {
      if (location?.state) {
        setPaperData(location.state);
        setPaperFound(true);
        return;
      }

      const source = papersData?.length ? papersData : await getData();
      const foundPaper = source.find((paper) => paper?.data?.id === id);

      if (foundPaper) {
        setPaperData(foundPaper.data);
        setPaperFound(true);
      } else {
        setPaperFound(false);
      }
    };

    fetchData();
  }, [id, location?.state, papersData, getData]);

  const checkFileSize = (file) => {
    const valid = file.size <= maxSize;
    if (!valid) message.error(`File must be smaller than ${maxSize / 1024 / 1024} MB!`);
    return valid;
  };

  const uploadProps = {
    showUploadList: true,
    multiple: true,
    onRemove: (file) => {
      const idx = fileList.indexOf(file);
      const newList = fileList.slice();
      newList.splice(idx, 1);
      setFileList(newList);
    },
    beforeUpload: (file) => {
      if (checkFileSize(file)) setFileList([...fileList, file]);
      return false;
    },
    fileList,
  };

  const setAdminData = async (links) => {
    const getID = () => {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
    };
    const docRef = doc(db, 'contributions', getID());
    if (currentUser?.email && currentUser?.uid && currentUser?.displayName) {
      await setDoc(docRef, {
        email: currentUser.email, uid: currentUser.uid,
        name: currentUser.displayName, contribution: links,
      });
    } else {
      await setDoc(docRef, {
        email: 'Anonymous', uid: 'Anonymous',
        name: 'Anonymous', contribution: links,
      });
    }
  };

  const saveSubject = async () => {
    try {
      if (!currentUser) { navigate('/signin'); return; }
      setSaveButtonLoading(true);
      let data = currentUser?.data;
      const docRef = doc(db, 'users', currentUser?.uid);

      if (saveSubjectText === 'Save Subject') {
        let saved = data?.saved ? [...data.saved, paperData.id] : [paperData.id];
        data = { ...data, saved };
        setUser({ ...currentUser, data });
        await setDoc(docRef, { displayName: data.displayName, email: data.email, uid: data.uid, saved });
        message.success('Saved');
        setSaveSubjectText('Unsave Subject');
      } else {
        let saved = (data?.saved || []).filter(item => item !== paperData.id);
        data = { ...data, saved };
        setUser({ ...currentUser, data });
        await setDoc(docRef, { displayName: data.displayName, email: data.email, uid: data.uid, saved });
        message.success('Removed');
        setSaveSubjectText('Save Subject');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSaveButtonLoading(false);
    }
  };

  const uploadFiles = async () => {
    setUploading(true);
    try {
      const urls = [];
      for (const file of fileList) {
        const fileName = `${Date.now()}_${file.name}`;
        const storageRef = ref(storage, fileName);
        await uploadBytesResumable(storageRef, file);
        const url = await getDownloadURL(storageRef);
        urls.push(url);
      }
      await setAdminData(urls);
      setFileList([]);
      message.success('Thanks for your contribution!');
    } catch {
      message.error('Upload failed.');
    } finally {
      setUploading(false);
      setShowModal(false);
    }
  };

  const uploadLinks = async () => {
    const newArray = linkData.map(el => el.columnName);
    const broken = newArray.filter((url) => !isUrl(url));
    if (broken.length) {
      message.error(`${broken.length} broken link(s) found`);
      return;
    }
    setUploading(true);
    try {
      await setAdminData(newArray);
      message.success('Thanks for your contribution!');
    } catch {
      message.error('Upload failed.');
    } finally {
      setUploading(false);
      setShowModal(false);
    }
  };

  const handleModalOk = () => {
    if (selectedTab === '1') uploadFiles();
    else if (selectedTab === '2') uploadLinks();
    else {
      const subject = 'Providing with Material';
      const mailtoLink = `mailto:muhammadsaboor119@gmail.com?subject=${encodeURIComponent(subject)}`;
      window.open(mailtoLink, '_blank');
    }
  };

  useEffect(() => {
    setOkText(selectedTab === '3' ? 'Send Email' : 'Upload');
  }, [selectedTab]);

  useEffect(() => {
    if (currentUser?.data?.saved) {
      const savedId = paperData?.id || id;
      setSaveSubjectText(
        currentUser.data.saved.indexOf(savedId) === -1 ? 'Save Subject' : 'Unsave Subject'
      );
    }
  }, [currentUser, id, paperData?.id]);

  useEffect(() => {
    document.title = (paperData?.title || 'Paper') + ' — SitsCom';
  }, [paperData]);

  let C_O = [{ column1: paperData?.CO_display || [], column2: paperData?.CO_link || [] }];
  if (!paperData?.CO_display || paperData?.CO_display?.length === 0) C_O = [];

  const items = [
    { key: '1', label: 'Upload Files' },
    { key: '2', label: 'Enter Links' },
    { key: '3', label: 'Email Material' },
  ];

  const handleAddRow = () => {
    setLinkData([...linkData, {
      key: String(linkData.length + 1),
      serialNumber: String(linkData.length + 1),
      columnName: `Link ${linkData.length + 1}`,
    }]);
  };

  const columns = [
    { title: '#', dataIndex: 'serialNumber', key: 'serialNumber', width: 60 },
    {
      title: 'Link', dataIndex: 'columnName', key: 'columnName',
      render: (text, record) => (
        <Input
          value={text}
          onChange={(e) => {
            const newData = [...linkData];
            newData.find(item => item.key === record.key).columnName = e.target.value;
            setLinkData(newData);
          }}
          className="input"
          placeholder="https://..."
        />
      ),
    },
  ];

  if (!paperFound) return <ErrorPage />;

  return (
    <>
      <Modal
        title="Contribute Material"
        centered
        open={showModal}
        onOk={handleModalOk}
        okText={okText}
        okType="default"
        confirmLoading={uploading}
        onCancel={() => setShowModal(false)}
      >
        <Tabs defaultActiveKey={selectedTab} onChange={setSelectedTab} items={items} />
        {selectedTab === '1' && (
          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Click or drag files to upload</p>
            <p className="ant-upload-hint">Max file size: 5 MB</p>
          </Dragger>
        )}
        {selectedTab === '2' && (
          <div>
            <Table dataSource={linkData} columns={columns} pagination={false} size="small" />
            <Space style={{ marginTop: 12 }}>
              <Button onClick={handleAddRow} size="small">Add Row</Button>
            </Space>
          </div>
        )}
        {selectedTab === '3' && (
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', padding: '16px 0' }}>
            If other options don't suit you, email us the material at<br />
            <strong>muhammadsaboor119@gmail.com</strong>
          </p>
        )}
      </Modal>

      <div className="paper-page page-container">
        <div className="paper-header">
          <div className="paper-breadcrumb">
            <Link to="/home">Subjects</Link>
            <span>/</span>
            <span>{paperData?.title || 'Loading...'}</span>
          </div>
          <h1>{paperData?.title || 'Loading...'}</h1>
          <p className="paper-desc">{paperData?.description || 'Loading...'}</p>
          <div className="paper-actions">
            <button
              className="btn btn-primary"
              onClick={saveSubject}
              disabled={saveButtonLoading}
            >
              {saveButtonLoading ? 'Saving...' : saveSubjectText}
            </button>
            <button className="btn btn-secondary" onClick={() => setShowModal(true)}>
              Contribute
            </button>
          </div>
        </div>

        <div className="paper-sections">
          <List documentdata={C_O || []} documentName="Course Outline" />
          <List documentdata={paperData.books || []} documentName="Books" />
          <List documentdata={paperData.pastPapers || []} documentName="Past Papers" />
          <List documentdata={paperData?.quiz || []} documentName="Quizzes" />
          <List documentdata={paperData?.assignments || []} documentName="Assignments" />
          <List documentdata={paperData?.others || []} documentName="Others" />
          <List documentdata={paperData?.labTasks || []} documentName="Lab Tasks" />
        </div>

        <div className="paper-contribute">
          <h3>Have more materials?</h3>
          <p>Share your notes, past papers, or any helpful resources with fellow students.</p>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            Upload Material
          </button>
        </div>
      </div>
    </>
  );
}

export default Paper;
